import {
  and,
  collectionGroup,
  doc,
  endBefore,
  getCountFromServer,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { Constants, I18n } from 'galarm-config'
import { DateTimeUtils } from 'galarm-shared'
import {
  Box,
  Button,
  CheckBox,
  Data,
  DataTable,
  Heading,
  Layer,
  Menu,
  Select,
  Spinner,
  Text
} from 'grommet'
import {
  Close,
  FormNext,
  FormPrevious,
  MoreVertical,
  Refresh,
  Search
} from 'grommet-icons'
import { isEmpty } from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import ViewIncidentEscalation from './ViewIncidentEscalation'
import { useDispatch } from 'react-redux'
import ActionCreators from '../actions/creators'
import UserContext from './UserContext'
import IncidentNotes from './IncidentNotes'
import GlobalConfig from '../GlobalConfig'
import Combobox from 'react-widgets/Combobox'
import { DropdownList } from 'react-widgets'
import { useSelector } from 'react-redux'
import { chunkArray } from '../utils/utils'

const STATUS_OPTIONS = [
  { label: I18n.t('notAcknowledged'), value: 'open' },
  { label: I18n.t('acknowledged'), value: 'acknowledged' },
  { label: I18n.t('closed'), value: 'closed' },
  { label: I18n.t('recovered'), value: 'recovered' }
]

const PRIORITY_OPTIONS = [
  { label: I18n.t('critical'), value: 1 },
  { label: I18n.t('high'), value: 2 },
  { label: I18n.t('medium'), value: 3 },
  { label: I18n.t('low'), value: 4 },
  { label: I18n.t('info'), value: 5 }
]

const IncidentsTable = () => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')
  const enterpriseUser = useRef(null)

  const dispatch = useDispatch()
  const { user, userRoles } = useContext(UserContext)

  const isIncidentCoordinator = userRoles.includes(
    Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  )

  const filterValues = useSelector(state => state.accountInfo.filterValues)
  const savedFilters = useSelector(state => state.accountInfo.incidentFilters)
  const members = useSelector(state => state.accountInfo.members)
  const alerts = useSelector(state => state.accountInfo.alerts)
  const [incidents, setIncidents] = useState([])
  const [lastVisible, setLastVisible] = useState(null)
  const [firstVisible, setFirstVisible] = useState(null)
  const [loading, setLoading] = useState(false)
  const [numIncidents, setNumIncidents] = useState(0)
  const [currentStartIndex, setCurrentStartIndex] = useState(1)
  // One buffer per chunk of alert ids, used only in multi-chunk mode (see below)
  const [chunkBuffers, setChunkBuffers] = useState([])

  const currentMember = members.find(m => m.id === user?.uid)

  const effectiveListenerIds = isIncidentCoordinator
    ? [
        ...new Set([
          ...(currentMember?.assignedListeners || []),
          ...alerts
            .filter(alert =>
              (alert.labels || []).some(labelId =>
                (currentMember?.assignedLabels || []).includes(labelId)
              )
            )
            .map(alert => alert.id)
        ])
      ]
    : []
  const [pageSize, setPageSize] = useState(savedFilters?.pageSize || 50)
  const [selectedIdentifier, setSelectedIdentifier] = useState(
    savedFilters?.identifier || ''
  )
  const [selectedAlertName, setSelectedAlertName] = useState(
    savedFilters?.alertName || ''
  )
  const [selectedReportedAt, setSelectedReportedAt] = useState(
    savedFilters?.reportedAt || ''
  )
  const [selectedPriority, setSelectedPriority] = useState(
    savedFilters?.priority || ''
  )
  const [selectedStatus, setSelectedStatus] = useState(
    savedFilters?.status || ''
  )
  const [sortOptions, setSortOptions] = useState(
    savedFilters?.sortOptions || {
      property: 'reportedAt',
      direction: 'desc'
    }
  )
  const [filtersLoaded, setFiltersLoaded] = useState(false)
  let unsubscribe = null

  useEffect(() => {
    const fetchEnterpriseUser = async () => {
      try {
        const enterpriseUserDoc = doc(
          GlobalConfig.firestoreDb,
          'enterpriseUsers',
          user.uid
        )
        const enterpriseUserSnap = await getDoc(enterpriseUserDoc)
        enterpriseUser.current = enterpriseUserSnap.data()

        if (enterpriseUser.current && !enterpriseUser.current.authToken) {
          const getGalarmApiAuthToken = httpsCallable(
            getFunctions(),
            'getGalarmApiAuthToken'
          )
          const authTokenResult = await getGalarmApiAuthToken()
          enterpriseUser.current = {
            ...enterpriseUser.current,
            authToken: authTokenResult.data.token
          }
        }

        // Load saved filters for this enterprise account
        const savedFilters =
          enterpriseUserSnap.data()?.incidentFilters?.[enterpriseAccountId]
        if (savedFilters) {
          setPageSize(savedFilters.pageSize || 50)
          setSelectedIdentifier(savedFilters.identifier || '')
          setSelectedAlertName(savedFilters.alertName || '')
          setSelectedReportedAt(savedFilters.reportedAt || '')
          setSelectedPriority(savedFilters.priority || '')
          setSelectedStatus(savedFilters.status || '')
          setSortOptions(
            savedFilters.sortOptions || {
              property: 'reportedAt',
              direction: 'desc'
            }
          )
          dispatch(ActionCreators.setIncidentFilters(savedFilters))
        }
      } catch (error) {
        console.error('Error fetching enterprise user:', error)
      } finally {
        setFiltersLoaded(true)
      }
    }
    fetchEnterpriseUser()
  }, [user, enterpriseAccountId, dispatch])

  // Save filters when they change (debounced)
  useEffect(() => {
    if (!filtersLoaded) return

    const filters = {
      pageSize,
      identifier: selectedIdentifier,
      alertName: selectedAlertName,
      reportedAt: selectedReportedAt,
      priority: selectedPriority,
      status: selectedStatus,
      sortOptions
    }

    // debounced
    const timeoutId = setTimeout(() => {
      dispatch(
        ActionCreators.saveIncidentFilters(
          filters,
          user.uid,
          enterpriseAccountId
        )
      )
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [
    filtersLoaded,
    pageSize,
    selectedIdentifier,
    selectedAlertName,
    selectedReportedAt,
    selectedPriority,
    selectedStatus,
    sortOptions,
    user.uid,
    enterpriseAccountId,
    dispatch
  ])

  const FIRESTORE_IN_LIMIT = 30
  const MULTI_CHUNK_BUFFER_LIMIT = 1000

  const getBaseWhereClauses = () => {
    const whereClauses = [
      where('enterpriseAccountId', '==', enterpriseAccountId)
    ]

    // Do an equality search on the identifier
    // as Firestore doesn't support more than one
    // inequality filter
    if (selectedIdentifier) {
      whereClauses.push(where('identifier', '==', selectedIdentifier))
    }

    if (selectedAlertName) {
      whereClauses.push(where('alertName', '==', selectedAlertName))
    }

    if (selectedReportedAt) {
      whereClauses.push(where('reportedAtDate', '==', selectedReportedAt))
    }

    if (selectedStatus) {
      whereClauses.push(
        where(
          'state',
          '==',
          STATUS_OPTIONS.find(s => s.label === selectedStatus).value
        )
      )
    }

    if (selectedPriority) {
      whereClauses.push(
        where(
          'priority',
          '==',
          PRIORITY_OPTIONS.find(p => p.label === selectedPriority).value
        )
      )
    }

    return whereClauses
  }

  // Firestore caps 'in'/'or' at 30 total disjunctions per query, so a coordinator
  // listening to more than 30 alerts can't be expressed as a single query - each
  // chunk gets its own query, merged and paginated client-side (see below)
  const alertIdChunks =
    isIncidentCoordinator && effectiveListenerIds.length > 0
      ? chunkArray(effectiveListenerIds, FIRESTORE_IN_LIMIT)
      : null
  const needsMultiChunkMode = !!alertIdChunks && alertIdChunks.length > 1

  const createIncidentsQuery = () => {
    if (isIncidentCoordinator && !currentMember) return null
    // Coordinator with no assigned listeners/labels must see nothing
    if (
      isIncidentCoordinator &&
      currentMember &&
      effectiveListenerIds.length === 0
    ) {
      return null
    }
    if (needsMultiChunkMode) return null // handled by the multi-chunk effect below

    const whereClauses = getBaseWhereClauses()
    if (alertIdChunks) {
      whereClauses.push(where('alertId', 'in', alertIdChunks[0]))
    }

    return query(
      collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
      and(...whereClauses),
      orderBy('timestamp', 'desc')
    )
  }

  const setupListener = queryInstance => {
    if (unsubscribe) unsubscribe()

    unsubscribe = onSnapshot(queryInstance, snapshot => {
      if (!snapshot.empty) {
        setFirstVisible(snapshot.docs[0])
        setLastVisible(snapshot.docs[snapshot.docs.length - 1])
        setIncidents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
      } else {
        setIncidents([])
      }
      fetchTotalIncidentsCount()
    })
  }

  // Multi-chunk mode: one buffered, real-time query per chunk of alert ids,
  // merged and sorted client-side since Firestore can't OR them in one query
  const mergedMultiChunkIncidents = useMemo(() => {
    if (!needsMultiChunkMode) return null
    return [...chunkBuffers.flat()].sort((a, b) => b.timestamp - a.timestamp)
  }, [needsMultiChunkMode, chunkBuffers])

  useEffect(() => {
    if (!needsMultiChunkMode) return

    // Reset buffers on (re)subscribe so stale chunks from a previous filter/listener
    // set can't linger - especially if the chunk count shrinks and an old index
    // would otherwise never get overwritten
    setChunkBuffers(alertIdChunks.map(() => []))

    const whereClauses = getBaseWhereClauses()
    const unsubscribes = alertIdChunks.map((chunk, chunkIndex) => {
      const chunkQuery = query(
        collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
        and(...whereClauses, where('alertId', 'in', chunk)),
        orderBy('timestamp', 'desc'),
        limit(MULTI_CHUNK_BUFFER_LIMIT)
      )
      return onSnapshot(chunkQuery, snapshot => {
        setChunkBuffers(prev => {
          const next = [...prev]
          next[chunkIndex] = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data()
          }))
          return next
        })
      })
    })

    return () => unsubscribes.forEach(unsub => unsub())
  }, [
    needsMultiChunkMode,
    enterpriseAccountId,
    selectedIdentifier,
    selectedAlertName,
    selectedReportedAt,
    selectedPriority,
    selectedStatus,
    JSON.stringify(effectiveListenerIds)
  ])

  useEffect(() => {
    if (!needsMultiChunkMode || !mergedMultiChunkIncidents) return
    const startIdx = currentStartIndex - 1
    setIncidents(mergedMultiChunkIncidents.slice(startIdx, startIdx + pageSize))
  }, [
    needsMultiChunkMode,
    mergedMultiChunkIncidents,
    currentStartIndex,
    pageSize
  ])

  useEffect(() => {
    fetchInitialIncidents()
    fetchTotalIncidentsCount()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [
    enterpriseAccountId,
    pageSize,
    selectedIdentifier,
    selectedAlertName,
    selectedReportedAt,
    selectedPriority,
    selectedStatus,
    currentMember,
    JSON.stringify(effectiveListenerIds)
  ])

  const goToFirstPage = () => {
    fetchInitialIncidents()
  }

  const onRefreshIncidentsTable = async () => {
    goToFirstPage()
    const functions = getFunctions()
    const updateFilterValues = httpsCallable(
      functions,
      'updateIncidentFilterValuesForEnterpriseAccount'
    )
    await updateFilterValues({ enterpriseAccountId })
  }

  const fetchTotalIncidentsCount = async () => {
    try {
      if (needsMultiChunkMode) {
        const whereClauses = getBaseWhereClauses()
        const counts = await Promise.all(
          alertIdChunks.map(chunk => {
            const chunkQuery = query(
              collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
              and(...whereClauses, where('alertId', 'in', chunk))
            )
            return getCountFromServer(chunkQuery).then(
              snapshot => snapshot.data().count
            )
          })
        )
        setNumIncidents(counts.reduce((sum, count) => sum + count, 0))
        return
      }

      const incidentsQuery = createIncidentsQuery()
      if (!incidentsQuery) {
        setNumIncidents(0)
        return
      }
      const incidentsCountQuery = query(incidentsQuery)
      const totalIncidentCountSnapshot = await getCountFromServer(
        incidentsCountQuery
      )
      setNumIncidents(totalIncidentCountSnapshot.data().count)
    } catch (error) {
      console.error('Error fetching total incidents: ', error)
    }
  }

  // const getSortOptionsForQuery = () => {
  //   if (sortOptions.property === 'reportedAt') {
  //     return ['timestamp', sortOptions.direction]
  //   }
  //   return [sortOptions.property, sortOptions.direction]
  // }

  const fetchInitialIncidents = async () => {
    setLoading(true)
    setCurrentStartIndex(1)

    if (needsMultiChunkMode) {
      // Buffers stream in via the multi-chunk effect above; just refresh the count
      fetchTotalIncidentsCount()
      setLoading(false)
      return
    }

    const incidentsQuery = createIncidentsQuery()
    if (!incidentsQuery) {
      if (unsubscribe) unsubscribe()
      setIncidents([])
      setNumIncidents(0)
      setLoading(false)
      return
    }
    const initialIncidentsQuery = query(incidentsQuery, limit(pageSize))
    setupListener(initialIncidentsQuery)
    setLoading(false)
  }

  const fetchNextPage = async () => {
    if (needsMultiChunkMode) {
      if (
        !mergedMultiChunkIncidents ||
        currentStartIndex - 1 + incidents.length >=
          mergedMultiChunkIncidents.length
      ) {
        return
      }
      setCurrentStartIndex(prevStartIndex => prevStartIndex + pageSize)
      return
    }

    if (!lastVisible) return

    setLoading(true)
    const incidentsQuery = createIncidentsQuery()
    if (!incidentsQuery) {
      setLoading(false)
      return
    }
    const nextPageQuery = query(
      incidentsQuery,
      startAfter(lastVisible),
      limit(pageSize)
    )
    setupListener(nextPageQuery)
    setCurrentStartIndex(currentStartIndex + pageSize)
    setLoading(false)
  }

  const fetchPreviousPage = async () => {
    if (needsMultiChunkMode) {
      if (currentStartIndex === 1) return
      setCurrentStartIndex(prevStartIndex =>
        Math.max(1, prevStartIndex - pageSize)
      )
      return
    }

    if (!firstVisible) return

    setLoading(true)
    const incidentsQuery = createIncidentsQuery()
    if (!incidentsQuery) {
      setLoading(false)
      return
    }
    const previousPageQuery = query(
      incidentsQuery,
      endBefore(firstVisible),
      limit(pageSize)
    )
    setupListener(previousPageQuery)
    setCurrentStartIndex(Math.max(1, currentStartIndex - pageSize))
    setLoading(false)
  }

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize)
  }

  const handleResetFilters = async () => {
    setSelectedIdentifier('')
    setSelectedAlertName('')
    setSelectedReportedAt('')
    setSelectedPriority('')
    setSelectedStatus('')
    setPageSize(50)
    setSortOptions({
      property: 'reportedAt',
      direction: 'desc'
    })

    // Clear saved filters in Firestore
    if (user?.uid && enterpriseAccountId) {
      dispatch(
        ActionCreators.saveIncidentFilters(
          {
            pageSize: 50,
            identifier: '',
            alertName: '',
            reportedAt: '',
            priority: '',
            status: '',
            sortOptions: {
              property: 'reportedAt',
              direction: 'desc'
            }
          },
          user.uid,
          enterpriseAccountId
        )
      )
    }

    goToFirstPage()
  }

  const [showIncidentEscalationDialog, setShowIncidentEscalationDialog] =
    useState({})
  const displayIncidentEscalationDialog = incident =>
    setShowIncidentEscalationDialog({ incident })
  const hideIncidentEscalationDialog = () => setShowIncidentEscalationDialog({})

  const [showIncidentNotesDialog, setShowIncidentNotesDialog] = useState({})
  const displayIncidentNotesDialog = incident =>
    setShowIncidentNotesDialog({ incident })
  const hideIncidentNotesDialog = () => setShowIncidentNotesDialog({})

  const onCloseIncident = async incident => {
    dispatch(ActionCreators.closeIncident(incident, enterpriseUser.current))
  }

  const onCloseIncidents = async () => {
    const selectedIncidents = incidents.filter(
      incident => incident.selected && incident.state === 'open'
    )
    selectedIncidents.forEach(incident => onCloseIncident(incident))
  }

  const getMenuItemsForIncident = incident => {
    const menuItems = [
      {
        label: I18n.t('viewIncidentNotes'),
        onClick: () => displayIncidentNotesDialog(incident)
      },
      {
        label: I18n.t('viewIncidentEscalation'),
        onClick: () => displayIncidentEscalationDialog(incident)
      }
    ]

    // Only show close incident if it is not already closed
    if (!incident.closedAt) {
      menuItems.push({
        label: I18n.t('closeIncident'),
        onClick: () => onCloseIncident(incident)
      })
    }

    return menuItems
  }

  const onSelectIncident = (incident, selected) => {
    const newIncidents = incidents.map(inc => {
      if (inc.id === incident.id) {
        return { ...inc, selected }
      }
      return inc
    })
    setIncidents(newIncidents)
  }

  const isIncidentValid = incident => 'alertId' in incident

  // let allIncidentsLastAssignees = []
  const tableData = incidents
    .map(incident => {
      // let lastAssignees = [],
      //   lastAssignedAt = ''
      // if (incident.alarms && incident.alarms.length) {
      //   const latestAlarm = Utils.objArrayMax(incident.alarms, 'date')
      //   lastAssignees = latestAlarm.members.map(member => ({
      //     label: member.name,
      //     value: member.id
      //   }))
      //   lastAssignedAt = DateTimeUtils.getDateTimeAsString(latestAlarm.date)
      // }
      // allIncidentsLastAssignees = unionWith(
      //   allIncidentsLastAssignees,
      //   lastAssignees,
      //   (a, b) => a.value === b.value
      // )

      return {
        id: incident.id,
        identifier: incident.identifier || incident.id,
        enterpriseAccountId: incident.enterpriseAccountId,
        alertId: incident.alertId,
        alertName: incident.alertName,
        reportedAtDate:
          incident.reportedAtDate ||
          DateTimeUtils.getDateAsString(incident.timestamp),
        reportedAt: incident.timestamp,
        state: incident.state,
        acknowledgements: incident.acknowledgements,
        closedBy: incident.closedBy,
        closedAt: incident.closedTimestamp,
        closedTimestamp: incident.closedTimestamp,
        recoveredAt: incident.recoveredTimestamp,
        recoveredTimestamp: incident.recoveredTimestamp,
        selected: incident.selected,
        timestamp: incident.timestamp,
        message: incident.message,
        additionalNotes: incident.additionalNotes,
        priority: incident.priority || 3
      }
    })
    .sort((a, b) => {
      if (a[sortOptions.property] < b[sortOptions.property]) {
        return sortOptions.direction === 'asc' ? -1 : 1
      }
      if (a[sortOptions.property] > b[sortOptions.property]) {
        return sortOptions.direction === 'asc' ? 1 : -1
      }
      return 0
    })

  const showClosedSelectedIncidentsButton = tableData.some(
    incident => incident.selected && incident.state === 'open'
  )

  return (
    <Box
      pad="small"
      width="xlarge"
      height={{ min: 'large' }}
      flex={{ shrink: 0 }}
      gap="xsmall">
      <Box direction="row" gap="small" justify="between" align="center">
        <Box direction="row" gap="small" align="center">
          <Heading margin="small" level={3}>
            {I18n.t('incidents')}
          </Heading>
          <Button
            icon={<Refresh color="primary" />}
            onClick={onRefreshIncidentsTable}
            margin={{ right: 'small' }}
          />
          <Button
            disabled={currentStartIndex === 1 || loading}
            icon={<FormPrevious color="primary" />}
            onClick={fetchPreviousPage}
          />
          <Text>
            {I18n.t('showingIncidents', {
              startIndex: currentStartIndex,
              endIndex: Math.min(
                currentStartIndex + incidents.length - 1,
                numIncidents
              ),
              count: numIncidents
            })}
          </Text>
          <Button
            disabled={
              // In multi-chunk mode numIncidents can exceed what's actually
              // buffered (see MULTI_CHUNK_BUFFER_LIMIT), so bound Next on the
              // merged buffer itself rather than the exact server count
              (needsMultiChunkMode
                ? !mergedMultiChunkIncidents ||
                  currentStartIndex - 1 + incidents.length >=
                    mergedMultiChunkIncidents.length
                : currentStartIndex + incidents.length - 1 >= numIncidents) ||
              loading
            }
            icon={<FormNext color="primary" />}
            onClick={fetchNextPage}
          />
        </Box>
        <Box direction="row" gap="small" align="center">
          {showClosedSelectedIncidentsButton &&
            [
              ...Constants.ENTERPRISE_EDITOR_ROLES,
              Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
            ].some(role => userRoles?.includes(role)) && (
              <Button
                size="small"
                label={I18n.t('closeSelectedIncidents')}
                onClick={onCloseIncidents}
              />
            )}
          <Box direction="row" align="center" width="small" gap="small">
            <Box>
              <Text size="small" color="darkTint">
                {I18n.t('incidentsPerPage')}
              </Text>
            </Box>
            <Select
              options={[10, 50, 100]}
              value={pageSize}
              onChange={({ option }) => handlePageSizeChange(option)}
            />
          </Box>
        </Box>
      </Box>
      {loading ? (
        <Box align="center" pad="large">
          <Spinner />
        </Box>
      ) : (
        <Box pad="small">
          {/* Add dropdowns for various filter options */}
          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ bottom: 'small' }}>
            <Search />
            <Combobox
              placeholder={I18n.t('identifier')}
              hideCaret
              hideEmptyPopup
              data={Object.keys(filterValues?.identifiers || {})}
              value={selectedIdentifier}
              onChange={setSelectedIdentifier}
            />
            <DropdownList
              placeholder={I18n.t('listener')}
              data={
                isIncidentCoordinator
                  ? alerts
                      .filter(alert => effectiveListenerIds.includes(alert.id))
                      .map(alert => alert.name)
                      .concat('')
                  : Object.keys(filterValues?.alertNames || {}).concat('')
              }
              value={selectedAlertName}
              onChange={setSelectedAlertName}
              renderListItem={({ item }) => (
                <Text>{item || I18n.t('allListeners')}</Text>
              )}
            />
            <DropdownList
              placeholder={I18n.t('reportedAt')}
              data={Object.keys(filterValues?.reportedAtDates || {}).concat('')}
              value={selectedReportedAt}
              onChange={setSelectedReportedAt}
              renderListItem={
                ({ item }) => <Text>{item || I18n.t('allDates')}</Text> // eslint-disable-line
              }
            />
            <DropdownList
              placeholder={I18n.t('priority')}
              data={Object.keys(filterValues?.priorities || {})
                .map(priority => {
                  return PRIORITY_OPTIONS.find(
                    p => p.value.toString() === priority
                  ).label
                })
                .concat('')}
              value={selectedPriority}
              onChange={setSelectedPriority}
              renderListItem={
                ({ item }) => <Text>{item || I18n.t('allPriorities')}</Text> // eslint-disable-line
              }
            />
            <DropdownList
              placeholder={I18n.t('status')}
              data={Object.keys(filterValues?.states || {})
                .map(state => STATUS_OPTIONS.find(s => s.value === state).label)
                .concat('')}
              value={selectedStatus}
              onChange={setSelectedStatus}
              renderListItem={
                ({ item }) => <Text>{item || I18n.t('allStatuses')}</Text> // eslint-disable-line
              }
            />
            {(selectedIdentifier ||
              selectedAlertName ||
              selectedReportedAt ||
              selectedPriority ||
              selectedStatus ||
              pageSize !== 50) && (
              <Button
                icon={<Close />}
                onClick={handleResetFilters}
                tip={I18n.t('resetFilters')}
                plain
              />
            )}
          </Box>
          <Data
            data={tableData}
            properties={{
              state: {
                label: I18n.t('status'),
                options: STATUS_OPTIONS,
                sort: false,
                search: false
              },
              alertName: {
                label: I18n.t('listener'),
                sort: false,
                search: false
              },
              reportedAtDate: {
                label: I18n.t('reportedAt'),
                sort: false,
                search: false
              },
              priority: {
                label: I18n.t('priority'),
                options: PRIORITY_OPTIONS,
                sort: false,
                search: false
              }
            }}>
            <DataTable
              primaryKey="id"
              columns={[
                {
                  property: 'identifier',
                  header: <Text weight={500}>{I18n.t('identifier')}</Text>,
                  render: datum => (
                    <Box direction="row" align="center" justify="between">
                      {datum.state === 'open' ? (
                        <Box direction="row" gap="small" justify="between">
                          <CheckBox
                            checked={datum.selected}
                            onChange={event =>
                              onSelectIncident(datum, event.target.checked)
                            }
                          />
                          <Text>{datum.identifier}</Text>
                        </Box>
                      ) : (
                        <Box direction="row" gap="small" justify="end">
                          <CheckBox disabled checked />
                          <Text>{datum.identifier}</Text>
                        </Box>
                      )}
                      <Menu
                        dropBackground="lightTint"
                        alignSelf="start"
                        size="small"
                        icon={<MoreVertical color="textColor" />}
                        items={getMenuItemsForIncident(datum)}
                      />
                    </Box>
                  )
                },
                {
                  property: 'alertName',
                  header: <Text weight={500}>{I18n.t('listener')}</Text>
                },
                {
                  property: 'reportedAt',
                  header: <Text weight={500}>{I18n.t('reportedAt')}</Text>,
                  render: datum => (
                    <Text>
                      {DateTimeUtils.getDateTimeAsString(datum.reportedAt)}
                    </Text>
                  )
                },
                {
                  property: 'priority',
                  header: <Text weight={500}>{I18n.t('priority')}</Text>,
                  render: datum => (
                    <Text>
                      {Constants.INCIDENT_PRIORITY_LABELS[datum.priority] ||
                        Constants.INCIDENT_PRIORITY_LABELS[3]}
                    </Text>
                  )
                },
                {
                  property: 'state',
                  header: <Text weight={500}>{I18n.t('status')}</Text>,
                  render: datum => (
                    <Text>{Constants.INCIDENT_STATUS_LABELS[datum.state]}</Text>
                  )
                },
                {
                  property: 'closedAt',
                  header: <Text weight={500}>{I18n.t('closedAtLabel')}</Text>,
                  render: datum => (
                    <Text>
                      {datum.closedAt
                        ? DateTimeUtils.getDateTimeAsString(datum.closedAt)
                        : datum.state === 'recovered'
                        ? DateTimeUtils.getDateTimeAsString(datum.recoveredAt)
                        : ''}
                    </Text>
                  )
                }
              ]}
              sort={{
                direction: sortOptions.direction,
                external: true,
                property: sortOptions.property
              }}
              background={{ header: 'light-4', body: ['light-1', 'white'] }}
              border={true}
              pad="small"
              onSort={setSortOptions}
            />
          </Data>
        </Box>
      )}
      {!isEmpty(showIncidentEscalationDialog) &&
        isIncidentValid(showIncidentEscalationDialog.incident) && (
          <Layer
            background="lightTint"
            onEsc={hideIncidentEscalationDialog}
            onClickOutside={hideIncidentEscalationDialog}>
            <Box margin="medium">
              <ViewIncidentEscalation
                incident={showIncidentEscalationDialog.incident}
                onClose={hideIncidentEscalationDialog}
              />
              <Box style={{ position: 'absolute', top: 20, right: 20 }}>
                <Close onClick={hideIncidentEscalationDialog} />
              </Box>
            </Box>
          </Layer>
        )}
      {!isEmpty(showIncidentNotesDialog) &&
        isIncidentValid(showIncidentNotesDialog.incident) && (
          <Layer
            background="lightTint"
            onEsc={hideIncidentNotesDialog}
            onClickOutside={hideIncidentNotesDialog}
            margin="medium">
            <Box margin="medium">
              <IncidentNotes
                incident={showIncidentNotesDialog.incident}
                onClose={hideIncidentNotesDialog}
              />
              <Box style={{ position: 'absolute', top: 20, right: 20 }}>
                <Close onClick={hideIncidentNotesDialog} />
              </Box>
            </Box>
          </Layer>
        )}
    </Box>
  )
}

export default IncidentsTable
